import { type BendSegmentInfo, Model } from 'metalworks-domain'
import { Shape } from 'three'

export interface CornerRadius {
  topLeft: number
  topRight: number
  bottomLeft: number
  bottomRight: number
}

export interface SegmentPosition {
  xStart: number
  xEnd: number
  yStart: number
  yEnd: number
}

/**
 * Function to create a circle shape from two arcs so that there are enough segments
 */
export const createCircleShape = (segmentShape: BendSegmentInfo) => {
  const { start, end } = segmentShape

  const horizontalSize = end.x - start.x
  const verticalSize = end.y - start.y

  const shape = new Shape()

  if (verticalSize > horizontalSize) {
    if (start.x === 0) {
      shape.absarc(end.x, end.y / 2, end.y / 2, Math.PI / 2, Math.PI * (6 / 4))
    } else {
      shape.absarc(start.x, end.y / 2, end.y / 2, Math.PI * (6 / 4), Math.PI / 2)
    }
  } else if (horizontalSize > verticalSize) {
    if (start.y === 0) {
      shape.absarc(end.x / 2, end.y, end.x / 2, Math.PI, 2 * Math.PI)
    } else {
      shape.absarc(end.x / 2, start.y, end.x / 2, 0, Math.PI)
    }
  } else {
    const radius = horizontalSize / 2

    shape.absarc(radius, radius, radius, 0, Math.PI)
    shape.absarc(radius, radius, radius, Math.PI, 2 * Math.PI)
  }

  return shape
}

/**
 * Function to create a rounded rectangle shape
 */
export const createRoundedRectShape = (segmentShape: Pick<BendSegmentInfo, 'shape' | 'start' | 'end'>) => {
  const shape = new Shape()

  if (segmentShape.shape.type !== 'roundedRect') {
    return shape
  }

  const {
    start,
    end,
    shape: { cornerRadius },
  } = segmentShape

  if (cornerRadius.bottomLeft > 0) {
    shape.moveTo(start.x + cornerRadius.bottomLeft, start.y)
  } else {
    shape.moveTo(start.x, start.y)
  }

  if (cornerRadius.bottomRight > 0) {
    shape.lineTo(end.x - cornerRadius.bottomRight, start.y)
    shape.quadraticCurveTo(end.x, start.y, end.x, start.y + cornerRadius.bottomRight)
  } else {
    shape.lineTo(end.x, start.y)
  }

  if (cornerRadius.topRight > 0) {
    shape.lineTo(end.x, end.y - cornerRadius.topRight)
    shape.quadraticCurveTo(end.x, end.y, end.x - cornerRadius.topRight, end.y)
  } else {
    shape.lineTo(end.x, end.y)
  }

  if (cornerRadius.topLeft > 0) {
    shape.lineTo(start.x + cornerRadius.topLeft, end.y)
    shape.quadraticCurveTo(start.x, end.y, start.x, end.y - cornerRadius.topLeft)
  } else {
    shape.lineTo(start.x, end.y)
  }

  if (cornerRadius.bottomLeft > 0) {
    shape.lineTo(start.x, start.y + cornerRadius.bottomLeft)
    shape.quadraticCurveTo(start.x, start.y, start.x + cornerRadius.bottomLeft, start.y)
  } else {
    shape.lineTo(start.x, start.y)
  }

  return shape
}

export const createTStrikeShape = (segmentInfo: BendSegmentInfo) => {
  const { shape: segmentShape } = segmentInfo

  const shape = new Shape()

  if (segmentShape.type !== 'tStrike') {
    return shape
  }

  const { mainStart, mainEnd, tongueStart, tongueEnd, mainCornerRadius, tongueCornerRadius, jointCornerRadius } =
    segmentShape
  const bottomJointOffset = Math.max(0, tongueStart.y - mainStart.y)
  const topJointOffset = Math.max(0, mainEnd.y - tongueEnd.y)
  const bottomJointCornerRadius = Math.min(jointCornerRadius, bottomJointOffset)
  const topJointCornerRadius = Math.min(jointCornerRadius, topJointOffset)

  if (segmentShape.mainStart.x < segmentShape.tongueStart.x) {
    shape.moveTo(mainStart.x + mainCornerRadius, mainStart.y)

    // Main - bottom right
    if (bottomJointOffset > 0) {
      shape.lineTo(mainEnd.x - mainCornerRadius, mainStart.y)
      shape.quadraticCurveTo(mainEnd.x, mainStart.y, mainEnd.x, mainStart.y + mainCornerRadius)
    } else {
      shape.lineTo(mainEnd.x, mainStart.y)
    }

    // Joint - bottom
    if (bottomJointCornerRadius > 0) {
      shape.lineTo(mainEnd.x, tongueStart.y - bottomJointCornerRadius)
      shape.quadraticCurveTo(mainEnd.x, tongueStart.y, tongueStart.x + bottomJointCornerRadius, tongueStart.y)
    } else {
      shape.lineTo(mainEnd.x, tongueStart.y)
      shape.lineTo(tongueStart.x, tongueStart.y)
    }

    if (tongueCornerRadius > 0) {
      // Tongue - bottom left
      shape.lineTo(tongueEnd.x - tongueCornerRadius, tongueStart.y)
      shape.quadraticCurveTo(tongueEnd.x, tongueStart.y, tongueEnd.x, tongueStart.y + tongueCornerRadius)
      // Tongue - top left
      shape.lineTo(tongueEnd.x, tongueEnd.y - tongueCornerRadius)
      shape.quadraticCurveTo(tongueEnd.x, tongueEnd.y, tongueEnd.x - tongueCornerRadius, tongueEnd.y)
    } else {
      shape.lineTo(tongueEnd.x, tongueStart.y)
      shape.lineTo(tongueEnd.x, tongueEnd.y)
    }

    // Joint - top left
    if (topJointCornerRadius > 0) {
      shape.lineTo(tongueStart.x + topJointCornerRadius, tongueEnd.y)
      shape.quadraticCurveTo(tongueStart.x, tongueEnd.y, tongueStart.x, tongueEnd.y + topJointCornerRadius)
    } else {
      shape.lineTo(tongueStart.x, tongueEnd.y)
    }
    // Main - top right
    if (topJointOffset > 0) {
      shape.lineTo(mainEnd.x, mainEnd.y - mainCornerRadius)
      shape.quadraticCurveTo(mainEnd.x, mainEnd.y, mainEnd.x - mainCornerRadius, mainEnd.y)
    } else {
      shape.lineTo(mainEnd.x, mainEnd.y)
    }
    // Main - top left
    shape.lineTo(mainStart.x + mainCornerRadius, mainEnd.y)
    shape.quadraticCurveTo(mainStart.x, mainEnd.y, mainStart.x, mainEnd.y - mainCornerRadius)
    shape.lineTo(mainStart.x, mainStart.y + mainCornerRadius)
    // Main - bottom left
    shape.quadraticCurveTo(mainStart.x, mainStart.y, mainStart.x + mainCornerRadius, mainStart.y)
    shape.lineTo(mainStart.x, mainStart.y)
  } else {
    shape.moveTo(mainStart.x + mainCornerRadius, mainStart.y)

    // Main - bottom right
    shape.lineTo(mainEnd.x - mainCornerRadius, mainStart.y)
    shape.quadraticCurveTo(mainEnd.x, mainStart.y, mainEnd.x, mainStart.y + mainCornerRadius)
    // Main - top right
    shape.lineTo(mainEnd.x, mainEnd.y - mainCornerRadius)
    shape.quadraticCurveTo(mainEnd.x, mainEnd.y, mainEnd.x - mainCornerRadius, mainEnd.y)
    // Main - top left
    if (topJointOffset > 0) {
      shape.lineTo(mainStart.x + mainCornerRadius, mainEnd.y)
      shape.quadraticCurveTo(mainStart.x, mainEnd.y, mainStart.x, mainEnd.y - mainCornerRadius)
    } else {
      shape.lineTo(mainStart.x, mainEnd.y)
    }
    // Joint - top
    if (topJointCornerRadius > 0) {
      shape.lineTo(mainStart.x, tongueEnd.y + topJointCornerRadius)
      shape.quadraticCurveTo(mainStart.x, tongueEnd.y, tongueEnd.x - topJointCornerRadius, tongueEnd.y)
    } else {
      shape.lineTo(mainStart.x, tongueEnd.y)
      shape.lineTo(tongueEnd.x, tongueEnd.y)
    }

    if (tongueCornerRadius > 0) {
      //  Tongue - top left
      shape.lineTo(tongueStart.x + tongueCornerRadius, tongueEnd.y)
      shape.quadraticCurveTo(tongueStart.x, tongueEnd.y, tongueStart.x, tongueEnd.y - tongueCornerRadius)
      // Tongue - bottom left
      shape.lineTo(tongueStart.x, tongueStart.y + tongueCornerRadius)
      shape.quadraticCurveTo(tongueStart.x, tongueStart.y, tongueStart.x + tongueCornerRadius, tongueStart.y)
    } else {
      shape.lineTo(tongueStart.x, tongueEnd.y)
      shape.lineTo(tongueStart.x, tongueStart.y)
    }

    // Join - bottom
    if (bottomJointCornerRadius > 0) {
      shape.lineTo(tongueEnd.x - bottomJointCornerRadius, tongueStart.y)
      shape.quadraticCurveTo(tongueEnd.x, tongueStart.y, tongueEnd.x, tongueStart.y - bottomJointCornerRadius)
    } else {
      shape.lineTo(tongueEnd.x, tongueStart.y)
    }

    // Main - bottom left
    if (bottomJointOffset > 0) {
      shape.lineTo(mainStart.x, mainStart.y + mainCornerRadius)
      shape.quadraticCurveTo(mainStart.x, mainStart.y, mainStart.x + mainCornerRadius, mainStart.y)
    } else {
      shape.lineTo(mainStart.x, mainStart.y)
    }
  }

  shape.closePath()

  return shape
}

export const createBendShape = (
  coords: { x: number; y: number },
  orientation: Model.BendOrientation,
  direction: Model.BendDirection,
  innerRadius: number,
  thickness: number
) => {
  const shape = new Shape()

  const outerRadius = innerRadius + thickness

  if (orientation === Model.BendOrientation.Horizontal && direction === Model.BendDirection.Front) {
    const zeroPoint = {
      x: coords.x + innerRadius,
      y: coords.y,
    }

    shape.moveTo(zeroPoint.x - outerRadius, zeroPoint.y)
    shape.quadraticCurveTo(zeroPoint.x - outerRadius, zeroPoint.y + outerRadius, zeroPoint.x, zeroPoint.y + outerRadius)
    shape.lineTo(zeroPoint.x, zeroPoint.y + innerRadius)
    shape.quadraticCurveTo(zeroPoint.x - innerRadius, zeroPoint.y + innerRadius, zeroPoint.x - innerRadius, zeroPoint.y)
  } else if (orientation === Model.BendOrientation.Horizontal && direction === Model.BendDirection.Back) {
    const zeroPoint = {
      x: coords.x - outerRadius,
      y: coords.y,
    }

    shape.moveTo(zeroPoint.x, zeroPoint.y + outerRadius)
    shape.quadraticCurveTo(zeroPoint.x + outerRadius, zeroPoint.y + outerRadius, zeroPoint.x + outerRadius, zeroPoint.y)
    shape.lineTo(zeroPoint.x + innerRadius, zeroPoint.y)
    shape.quadraticCurveTo(zeroPoint.x + innerRadius, zeroPoint.y + innerRadius, zeroPoint.x, zeroPoint.y + innerRadius)
  } else if (orientation === Model.BendOrientation.Vertical && direction === Model.BendDirection.Front) {
    const zeroPoint = {
      x: coords.x,
      y: coords.y + innerRadius,
    }

    shape.moveTo(zeroPoint.x, zeroPoint.y - outerRadius)
    shape.quadraticCurveTo(zeroPoint.x + outerRadius, zeroPoint.y - outerRadius, zeroPoint.x + outerRadius, zeroPoint.y)
    shape.lineTo(zeroPoint.x + innerRadius, zeroPoint.y)
    shape.quadraticCurveTo(zeroPoint.x + innerRadius, zeroPoint.y - innerRadius, zeroPoint.x, zeroPoint.y - innerRadius)
  } else if (orientation === Model.BendOrientation.Vertical && direction === Model.BendDirection.Back) {
    const zeroPoint = {
      x: coords.x,
      y: coords.y - outerRadius,
    }

    shape.moveTo(zeroPoint.x, zeroPoint.y + outerRadius)
    shape.quadraticCurveTo(zeroPoint.x + outerRadius, zeroPoint.y + outerRadius, zeroPoint.x + outerRadius, zeroPoint.y)
    shape.lineTo(zeroPoint.x + innerRadius, zeroPoint.y)
    shape.quadraticCurveTo(zeroPoint.x + innerRadius, zeroPoint.y + innerRadius, zeroPoint.x, zeroPoint.y + innerRadius)
  }

  shape.closePath()

  return shape
}
